import { createStore, applyMiddleware } from "redux";
import socketManager from "./socketManager";
import buildAuthorizeUrl from "./buildAuthorizeUrl";
import buildLogoutUrl from "./buildLogoutUrl";
import { stringNorm } from "./stringNorm";
import { testAccountStatus, testDeploymentRole, testPasswordStatus, testTerm } from "./filterUsers";

const store = createStore(reducer, getInitState(), applyMiddleware(triggers));
export default store;
var siteMeta;

store.assertMeta = function (siteMetadata) {
    if (!siteMeta && siteMetadata) {
        siteMeta = { ...siteMetadata };

        socketManager.init({
            store,
            ...siteMetadata,
        });
    }
};

function getInitState() {
    return {
        socket_ready: false,
        socket_status: "pending", // pending, ready, retry, failed
        auth_state_sync: false,
        pending_auth_code: null,
        pending_auth_code_location: null, // ! HACKHACK

        current_user_vuid: null,
        current_user_email: null,
        current_user_first_name: null,
        current_user_last_name: null,
        current_user_cog_sub_id: null,
        current_user_system_role: null,
        current_user_deployment_roles: null,
        current_user_deployment_data: null,
        current_user_vera_account_status: null,
        current_user_vera_password_status: null,

        all_deployments: null,
        current_user_active_deployment_id: null,
        current_user_active_deployment_role: null,
        current_user_active_deployment_data: null,

        // TODO: deployment_info...

        deployment_users: null,
        deployment_portals: null,
        custom_field_definitions: null,
        navguide_items: null,

        user_prdso_activity: null,
        prdso_courses: null,
        prdso_course_agg_time: null,
        prdso_deployment_agg_time_cum: null,
        prdso_deployment_agg_time_monthly: null,

        adding_user: false,
        adding_bulk_user: false,
        file_selected: false,
        adding_custom_field_definition: false,
        adding_navguide_item: false,

        new_user: null,
        file_det: null,
        new_custom_field_definition: null,
        new_navguide_item: null,

        editing_user: false,
        editing_custom_field_definition: false,
        editing_navguide_item: false,

        edited_user_id: null,
        edited_custom_field_definition_id: null,
        edited_navguide_item_id: null,

        edited_user: null,
        edited_custom_field_definition: null,
        edited_navguide_item: null,

        staff_user_list_filtered: [],
        staff_user_list_filter_term: "",
        staff_user_list_filter_deployment_role: null,
        staff_user_list_filter_account_status: null,
        staff_user_list_filter_password_status: null,

        public_user_list_filtered: [],
        public_user_list_filter_term: "",
        public_user_list_filter_deployment_role: null,
        public_user_list_filter_account_status: null,
        public_user_list_filter_password_status: null,

        custom_field_definition_list_filtered: [],
        custom_field_definition_list_filter_term: "",

        navguide_list_filtered: [],
        navguide_list_filter_term: "",
    };
}

function reducer(prevState, action) {
    /* eslint-disable-next-line no-console */
    // console.log(action);

    if (!prevState) {
        /* eslint-disable-next-line no-param-reassign */
        prevState = getInitState();
    }

    if (action.type === "SOCKET_UP") {
        return {
            ...prevState,
            socket_ready: true,
            socket_status: "ready",
        };
    }

    if (action.type === "SOCKET_DOWN") {
        return {
            ...prevState,
            socket_ready: false,
            // TODO: socket_status (need distinct granular actions)
        };
    }

    if (action.type === "RECEIVE_AUTH_STATE") {
        return {
            ...prevState,
            auth_state_sync: true,
            new_user: !action.data.current_user_vuid,
            current_user_vuid: action.data.current_user_vuid || null,
            current_user_email: action.data.current_user_email || null,
            current_user_first_name: action.data.current_user_first_name || null,
            current_user_last_name: action.data.current_user_last_name || null,
            current_user_cog_sub_id: action.data.current_user_cog_sub_id || null,
            current_user_system_role: action.data.current_user_system_role || null,
            current_user_deployment_roles: action.data.current_user_deployment_roles || null,
            current_user_deployment_data: action.data.current_user_deployment_data || null,
            current_user_vera_account_status: action.data.current_user_vera_account_status || null,
            current_user_vera_password_status: action.data.current_user_vera_password_status || null,

            all_deployments: action.data.all_deployments || null,
            current_user_active_deployment_id: action.data.current_user_active_deployment_id || null,
            current_user_active_deployment_role: action.data.current_user_active_deployment_role || null,
            current_user_active_deployment_data: action.data.current_user_active_deployment_data || null,
        };
    }

    if (action.type === "CLEAR_AUTH_STATE") {
        return {
            ...prevState,
            auth_state_sync: true,
            pending_auth_code: null,
            pending_auth_code_location: null, // ! HACKHACK
            new_user: false,
            current_user_vuid: null,
            current_user_email: null,
            current_user_first_name: null,
            current_user_last_name: null,
            current_user_cog_sub_id: null,
            current_user_system_role: null,
            current_user_deployment_roles: null,
            current_user_deployment_data: null,
            current_user_vera_account_status: null,
            current_user_vera_password_status: null,

            all_deployments: null,
            current_user_active_deployment_id: null,
            current_user_active_deployment_role: null,
            current_user_active_deployment_data: null,
        };
    }

    if (action.type === "UI_LOGIN") {
        return {
            ...prevState,
        };
    }

    if (action.type === "UI_LOGOUT") {
        return {
            ...prevState,
        };
    }

    if (action.type === "RECEIVE_AUTH_CODE") {
        return {
            ...prevState,
            pending_auth_code: action.data.auth_code,
            pending_auth_code_location: action.data.location, // ! HACKHACK
        };
    }

    if (action.type === "CLEAR_PENDING_AUTH_CODE") {
        return {
            ...prevState,
            pending_auth_code: null,
            pending_auth_code_location: null, // ! HACKHACK
        };
    }

    if (action.type === "SET_ACTIVE_DEPLOYMENT") {
        return {
            ...prevState,
        };
    }

    if (action.type === "CLEAR_ACTIVE_DEPLOYMENT") {
        return {
            ...prevState,
        };
    }

    if (action.type === "RECEIVE_DEPLOYMENT_INFO") {
        return {
            ...prevState,

            // TODO
        };
    }

    if (action.type === "RECEIVE_DEPLOYMENT_USERS") {
        let deployment_users = {};

        for (let user of action.data.deploymentUsers) {
            deployment_users[user.vuid] = user;
        }

        return {
            ...prevState,
            deployment_users,
        };
    }

    if (action.type === "START_EDITING_USER") {
        if (!action.data.vuid) return prevState;
        const existingUser = prevState.deployment_users[action.data.vuid];
        if (!existingUser) return prevState;

        return {
            ...prevState,
            editing_user: true,
            edited_user_id: action.data.vuid,
            edited_user: {
                first_name: existingUser.first_name,
                last_name: existingUser.last_name,
                email: existingUser.email,
                deployment_case_ids: existingUser.deployment_case_id,
                mentor_designation: existingUser.mentor_designation,
                cohort_name: existingUser.cohort_name,
            },
        };
    }

    if (action.type === "DONE_EDITING_USER") {
        return {
            ...prevState,
            editing_user: false,
            edited_user_id: null,
            edited_user: null,
        };
    }

    if (action.type === "EDIT_USER_FIRST_NAME") {
        return {
            ...prevState,
            edited_user: {
                ...prevState.edited_user,
                first_name: action.data.first_name || "",
            },
        };
    }

    if (action.type === "EDIT_USER_LAST_NAME") {
        return {
            ...prevState,
            edited_user: {
                ...prevState.edited_user,
                last_name: action.data.last_name || "",
            },
        };
    }

    if (action.type === "EDIT_USER_EMAIL") {
        return {
            ...prevState,
            edited_user: {
                ...prevState.edited_user,
                email: action.data.email || "",
            },
        };
    }

    if (action.type === "EDIT_USER_COHORT_NAME") {
        return {
            ...prevState,
            edited_user: {
                ...prevState.edited_user,
                cohort_name: action.data.cohort_name || "",
            },
        };
    }

    if (action.type === "EDIT_USER_MENTOR_DEISGNATION") {
        return {
            ...prevState,
            edited_user: {
                ...prevState.edited_user,
                mentor_designation: action.data.mentor_designation || false,
            },
        };
    }

    if (action.type === "EDIT_USER_DEPLOYMENT_CASE_ID") {
        let newDeploymentCaseIds = prevState.edited_user.deployment_case_ids || {};

        if (action.data.deployment_case_id) {
            newDeploymentCaseIds[action.data.deployment_id] = action.data.deployment_case_id;
        }

        return {
            ...prevState,
            edited_user: {
                ...prevState.edited_user,
                deployment_case_ids: newDeploymentCaseIds,
            },
        };
    }

    if (action.type === "START_ADDING_USER") {
        return {
            ...prevState,
            adding_user: true,
            new_user: {
                first_name: "",
                last_name: "",
                cohort_name: "",
                mentor_designation: false,
                email: "",
                system_role: "external",
                deployment_roles: {},
                deployment_case_ids: {},
                send_email: true,
                specify_password: false,
                password: "",
                force_change_password: false,
            },
        };
    }

    if (action.type === "DONE_ADDING_USER") {
        return {
            ...prevState,
            adding_user: false,
            new_user: null,
        };
    }

    if (action.type === "EDIT_NEW_USER_FIRST_NAME") {
        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                first_name: action.data.first_name || "",
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_LAST_NAME") {
        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                last_name: action.data.last_name || "",
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_COHORT_NAME") {
        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                cohort_name: action.data.cohort_name || "",
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_MENTOR_DEISGNATION") {
        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                mentor_designation: action.data.mentor_designation || false,
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_EMAIL") {
        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                email: action.data.email || "",
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_SYSTEM_ROLE") {
        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                system_role: action.data.system_role || "",
            },
        };
    }

    // if (action.type === 'EDIT_NEW_USER_DEPLOYMENT_ROLE') {
    //   return {
    //     ...prevState,
    //     new_user: {
    //       ...prevState.new_user,
    //       deployment_id: action.data.deployment_id || '',
    //       role: action.data.role || '',
    //     },
    //   }
    // }

    if (action.type === "RECEIVE_CLUSTER_DEPLOYMENTS") {
        let cluster_deployments = {};

        for (let deployment of action.data.clusterDeployments) {
            cluster_deployments[deployment.deployment_id] = deployment;
        }

        return {
            ...prevState,
            cluster_deployments,
        };
    }

    if (action.type === "EDIT_NEW_USER_DEPLOYMENT_ROLE") {
        if (prevState.new_user.system_role === "super" || prevState.new_user.system_role === "pcg") return prevState;

        let newDeploymentRoles = prevState.new_user.deployment_roles;

        if (action.data.role) {
            newDeploymentRoles[action.data.deployment_id] = action.data.role;
        } else {
            newDeploymentRoles[action.data.deployment_id] = undefined;
            delete newDeploymentRoles[action.data.deployment_id];
        }

        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                deployment_roles: newDeploymentRoles,
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_DEPLOYMENT_CASE_ID") {
        let newDeploymentCaseIds = prevState.new_user.deployment_case_ids || {};

        if (action.data.deployment_case_id) {
            newDeploymentCaseIds[action.data.deployment_id] = action.data.deployment_case_id;
        }

        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                deployment_case_ids: newDeploymentCaseIds,
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_SEND_WELCOME_EMAIL") {
        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                send_email: !!action.data.send_email,
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_SPECIFY_PASSWORD") {
        let newPassword = prevState.new_user.password;
        let newForceChange = prevState.new_user.force_change_password;

        if (!action.data.specify_password) {
            newPassword = "";
            newForceChange = false;
        }

        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                specify_password: !!action.data.specify_password,
                password: newPassword,
                force_change_password: newForceChange,
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_PASSWORD") {
        if (!prevState.new_user.specify_password) return prevState;

        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                password: action.data.password,
            },
        };
    }

    if (action.type === "EDIT_NEW_USER_FORCE_CHANGE_PASSWORD") {
        if (!prevState.new_user.specify_password) return prevState;

        return {
            ...prevState,
            new_user: {
                ...prevState.new_user,
                force_change_password: !!action.data.force_change_password,
            },
        };
    }

    if (action.type === "APPLY_STAFF_USER_LIST_FILTERS") {
        const term = stringNorm(prevState.staff_user_list_filter_term);
        const deploymentRole = prevState.staff_user_list_filter_deployment_role;
        const accountStatus = prevState.staff_user_list_filter_account_status;
        const passwordStatus = prevState.staff_user_list_filter_password_status;

        let filteredList = Object.keys(prevState.deployment_users || {}).filter((vuid) => {
            const user = prevState.deployment_users[vuid];

            if (term && !testTerm({ user, term })) return false;
            if (deploymentRole && !testDeploymentRole({ user, deploymentRole })) return false;
            if (accountStatus && !testAccountStatus({ user, accountStatus })) return false;
            if (passwordStatus && !testPasswordStatus({ user, passwordStatus })) return false;

            return true;
        });

        return {
            ...prevState,
            staff_user_list_filtered: filteredList,
        };
    }

    if (action.type === "CLEAR_STAFF_USER_LIST_FILTERS_ALL") {
        return {
            ...prevState,
            staff_user_list_filter_term: "",
            staff_user_list_filter_system_role: null,
            staff_user_list_filter_deployment: null,
            staff_user_list_filter_deployment_role: null,
            staff_user_list_filter_account_status: null,
            staff_user_list_filter_password_status: null,
        };
    }

    if (action.type === "SET_STAFF_USER_LIST_FILTER_TERM") {
        return {
            ...prevState,
            staff_user_list_filter_term: action.data.term,
        };
    }

    if (action.type === "CLEAR_STAFF_USER_LIST_FILTER_TERM") {
        return {
            ...prevState,
            staff_user_list_filter_term: "",
        };
    }

    if (action.type === "TOGGLE_STAFF_USER_LIST_FILTER_DEPLOYMENT_ROLE") {
        const prev = prevState.staff_user_list_filter_deployment_role || [];
        let newValue;

        if (prev.indexOf(action.data.deployment_role) === -1) {
            newValue = [...prev, action.data.deployment_role];
        } else {
            newValue = prev.filter((item) => item !== action.data.deployment_role);
        }

        return {
            ...prevState,
            staff_user_list_filter_deployment_role: newValue,
        };
    }

    if (action.type === "CLEAR_STAFF_USER_LIST_FILTER_DEPLOYMENT_ROLE") {
        return {
            ...prevState,
            staff_user_list_filter_deployment_role: null,
        };
    }

    if (action.type === "TOGGLE_STAFF_USER_LIST_FILTER_ACCOUNT_STATUS") {
        const prev = prevState.staff_user_list_filter_account_status || [];
        let newValue;

        if (prev.indexOf(action.data.vera_account_status) === -1) {
            newValue = [...prev, action.data.vera_account_status];
        } else {
            newValue = prev.filter((item) => item !== action.data.vera_account_status);
        }

        return {
            ...prevState,
            staff_user_list_filter_account_status: newValue,
        };
    }

    if (action.type === "CLEAR_STAFF_USER_LIST_FILTER_ACCOUNT_STATUS") {
        return {
            ...prevState,
            staff_user_list_filter_account_status: null,
        };
    }

    if (action.type === "TOGGLE_STAFF_USER_LIST_FILTER_PASSWORD_STATUS") {
        const prev = prevState.staff_user_list_filter_password_status || [];
        let newValue;

        if (prev.indexOf(action.data.vera_password_status) === -1) {
            newValue = [...prev, action.data.vera_password_status];
        } else {
            newValue = prev.filter((item) => item !== action.data.vera_password_status);
        }

        return {
            ...prevState,
            staff_user_list_filter_password_status: newValue,
        };
    }

    if (action.type === "CLEAR_STAFF_USER_LIST_FILTER_PASSWORD_STATUS") {
        return {
            ...prevState,
            staff_user_list_filter_password_status: null,
        };
    }

    if (action.type === "APPLY_PUBLIC_USER_LIST_FILTERS") {
        const term = stringNorm(prevState.public_user_list_filter_term);
        const deploymentRole = prevState.public_user_list_filter_deployment_role;
        const accountStatus = prevState.public_user_list_filter_account_status;
        const passwordStatus = prevState.public_user_list_filter_password_status;

        let filteredList = Object.keys(prevState.deployment_users || {}).filter((vuid) => {
            const user = prevState.deployment_users[vuid];

            if (term && !testTerm({ user, term })) return false;
            if (deploymentRole && !testDeploymentRole({ user, deploymentRole })) return false;
            if (accountStatus && !testAccountStatus({ user, accountStatus })) return false;
            if (passwordStatus && !testPasswordStatus({ user, passwordStatus })) return false;

            return true;
        });

        return {
            ...prevState,
            public_user_list_filtered: filteredList,
        };
    }

    if (action.type === "CLEAR_PUBLIC_USER_LIST_FILTERS_ALL") {
        return {
            ...prevState,
            public_user_list_filter_term: "",
            public_user_list_filter_system_role: null,
            public_user_list_filter_deployment: null,
            public_user_list_filter_deployment_role: null,
            public_user_list_filter_account_status: null,
            public_user_list_filter_password_status: null,
        };
    }

    if (action.type === "SET_PUBLIC_USER_LIST_FILTER_TERM") {
        return {
            ...prevState,
            public_user_list_filter_term: action.data.term,
        };
    }

    if (action.type === "CLEAR_PUBLIC_USER_LIST_FILTER_TERM") {
        return {
            ...prevState,
            public_user_list_filter_term: "",
        };
    }

    if (action.type === "TOGGLE_PUBLIC_USER_LIST_FILTER_DEPLOYMENT_ROLE") {
        const prev = prevState.public_user_list_filter_deployment_role || [];
        let newValue;

        if (prev.indexOf(action.data.deployment_role) === -1) {
            newValue = [...prev, action.data.deployment_role];
        } else {
            newValue = prev.filter((item) => item !== action.data.deployment_role);
        }

        return {
            ...prevState,
            public_user_list_filter_deployment_role: newValue,
        };
    }

    if (action.type === "CLEAR_PUBLIC_USER_LIST_FILTER_DEPLOYMENT_ROLE") {
        return {
            ...prevState,
            public_user_list_filter_deployment_role: null,
        };
    }

    if (action.type === "TOGGLE_PUBLIC_USER_LIST_FILTER_ACCOUNT_STATUS") {
        const prev = prevState.public_user_list_filter_account_status || [];
        let newValue;

        if (prev.indexOf(action.data.vera_account_status) === -1) {
            newValue = [...prev, action.data.vera_account_status];
        } else {
            newValue = prev.filter((item) => item !== action.data.vera_account_status);
        }

        return {
            ...prevState,
            public_user_list_filter_account_status: newValue,
        };
    }

    if (action.type === "CLEAR_PUBLIC_USER_LIST_FILTER_ACCOUNT_STATUS") {
        return {
            ...prevState,
            public_user_list_filter_account_status: null,
        };
    }

    if (action.type === "TOGGLE_PUBLIC_USER_LIST_FILTER_PASSWORD_STATUS") {
        const prev = prevState.public_user_list_filter_password_status || [];
        let newValue;

        if (prev.indexOf(action.data.vera_password_status) === -1) {
            newValue = [...prev, action.data.vera_password_status];
        } else {
            newValue = prev.filter((item) => item !== action.data.vera_password_status);
        }

        return {
            ...prevState,
            public_user_list_filter_password_status: newValue,
        };
    }

    if (action.type === "CLEAR_PUBLIC_USER_LIST_FILTER_PASSWORD_STATUS") {
        return {
            ...prevState,
            public_user_list_filter_password_status: null,
        };
    }

    if (action.type === "RECEIVE_CUSTOM_FIELD_DEFINITIONS") {
        const customFieldDefinitions = {};

        for (let def of action.data.custom_field_definitions) {
            customFieldDefinitions[def.custom_field_definition_id] = def;
        }

        return {
            ...prevState,
            custom_field_definitions: customFieldDefinitions,
        };
    }

    if (action.type === "START_ADDING_CUSTOM_FIELD_DEFINITION") {
        return {
            ...prevState,
            adding_custom_field_definition: true,
            new_custom_field_definition: {
                field_name: "",
                field_type: "text",
                field_description: "",
            },
        };
    }

    if (action.type === "DONE_ADDING_CUSTOM_FIELD_DEFINITION") {
        return {
            ...prevState,
            adding_custom_field_definition: true,
            new_custom_field_definition: null,
        };
    }

    if (action.type === "EDIT_NEW_CUSTOM_FIELD_DEFINITION_NAME") {
        return {
            ...prevState,
            new_custom_field_definition: {
                ...prevState.new_custom_field_definition,
                field_name: action.data.field_name,
            },
        };
    }

    if (action.type === "EDIT_NEW_CUSTOM_FIELD_DEFINITION_TYPE") {
        // TODO: validate in allowed set

        return {
            ...prevState,
            new_custom_field_definition: {
                ...prevState.new_custom_field_definition,
                field_type: action.data.field_type,
            },
        };
    }

    if (action.type === "EDIT_NEW_CUSTOM_FIELD_DEFINITION_DESCRIPTION") {
        return {
            ...prevState,
            new_custom_field_definition: {
                ...prevState.new_custom_field_definition,
                field_description: action.data.field_description,
            },
        };
    }

    if (action.type === "START_EDITING_CUSTOM_FIELD_DEFINITION_NAME") {
        const existingDef = prevState.custom_field_definitions[action.data.custom_field_definition_id];
        if (!existingDef)
            return {
                ...prevState,
            };

        return {
            ...prevState,
            editing_custom_field_definition_name: true,
            edited_custom_field_definition_id: action.data.custom_field_definition_id,
            edited_custom_field_definition_name: existingDef.field_name,
        };
    }

    if (action.type === "EDIT_CUSTOM_FIELD_DEFINITION_NAME") {
        return {
            ...prevState,
            edited_custom_field_definition_name: action.data.field_name,
        };
    }

    if (action.type === "DONE_EDITING_CUSTOM_FIELD_DEFINITION_NAME") {
        return {
            ...prevState,
            editing_custom_field_definition_name: false,
            edited_custom_field_definition_id: null,
            edited_custom_field_definition_name: null,
        };
    }

    if (action.type === "START_EDITING_CUSTOM_FIELD_DEFINITION_DESCRIPTION") {
        const existingDef = prevState.custom_field_fefinitions[action.data.custom_field_definition_id];
        if (!existingDef)
            return {
                ...prevState,
            };

        return {
            ...prevState,
            editing_custom_field_definition_description: true,
            edited_custom_field_definition_id: action.data.custom_field_definition_id,
            edited_custom_field_definition_description: existingDef.field_description,
        };
    }

    if (action.type === "EDIT_CUSTOM_FIELD_DEFINITION_DESCRIPTION") {
        return {
            ...prevState,
            edited_custom_field_definition_description: action.data.field_description,
        };
    }

    if (action.type === "DONE_EDITING_CUSTOM_FIELD_DEFINITION_DESCRIPTION") {
        return {
            ...prevState,
            editing_custom_field_definition_description: false,
            edited_custom_field_definition_id: null,
            edited_custom_field_definition_description: null,
        };
    }

    if (action.type === "START_EDITING_NAVGUIDE_ITEM") {
        const existingNavItem = prevState.navguide_items[action.data.navguide_item_id];
        if (!existingNavItem)
            return {
                ...prevState,
            };

        return {
            ...prevState,
            editing_navguide_item: true,
            edited_navguide_item_id: action.data.navguide_item_id,
            edited_navguide_item: {
                item_name: existingNavItem.item_name,
                phone: existingNavItem.phone,
                email: existingNavItem.email,
                address_street: existingNavItem.address_street,
                address_city: existingNavItem.address_city,
                address_state: existingNavItem.address_state,
                address_zip: existingNavItem.address_zip,
                hours: existingNavItem.hours,
            },
        };
    }

    if (action.type === "DONE_EDITING_NAVGUIDE_ITEM") {
        return {
            ...prevState,
            editing_navguide_item: false,
            edited_navguide_item_id: null,
            edited_navguide_item: null,
        };
    }

    if (action.type === "EDIT_NAVGUIDE_ITEM_NAME") {
        return {
            ...prevState,
            edited_navguide_item: {
                ...prevState.edited_navguide_item,
                item_name: action.data.item_name,
            },
        };
    }

    if (action.type === "EDIT_NAVGUIDE_ITEM_HOURS") {
        return {
            ...prevState,
            edited_navguide_item: {
                ...prevState.edited_navguide_item,
                hours: action.data.hours,
            },
        };
    }

    if (action.type === "EDIT_NAVGUIDE_ITEM_EMAIL") {
        return {
            ...prevState,
            edited_navguide_item: {
                ...prevState.edited_navguide_item,
                email: action.data.email,
            },
        };
    }

    if (action.type === "EDIT_NAVGUIDE_ITEM_PHONE") {
        return {
            ...prevState,
            edited_navguide_item: {
                ...prevState.edited_navguide_item,
                phone: action.data.phone,
            },
        };
    }

    if (action.type === "EDIT_NAVGUIDE_ITEM_ADDRESS_STREET") {
        return {
            ...prevState,
            edited_navguide_item: {
                ...prevState.edited_navguide_item,
                address_street: action.data.address_street,
            },
        };
    }

    if (action.type === "EDIT_NAVGUIDE_ITEM_ADDRESS_CITY") {
        return {
            ...prevState,
            edited_navguide_item: {
                ...prevState.edited_navguide_item,
                address_city: action.data.address_city,
            },
        };
    }

    if (action.type === "EDIT_NAVGUIDE_ITEM_ADDRESS_STATE") {
        return {
            ...prevState,
            edited_navguide_item: {
                ...prevState.edited_navguide_item,
                address_state: action.data.address_state,
            },
        };
    }

    if (action.type === "EDIT_NAVGUIDE_ITEM_ADDRESS_ZIP") {
        return {
            ...prevState,
            edited_navguide_item: {
                ...prevState.edited_navguide_item,
                address_zip: action.data.address_zip,
            },
        };
    }

    if (action.type === "START_ADDING_NAVGUIDE_ITEM") {
        return {
            ...prevState,
            adding_navguide_item: true,
            new_navguide_item: {
                item_name: "",
                phone: "",
                email: "",
                address_street: "",
                address_city: "",
                address_state: "",
                address_zip: "",
                hours: "",
            },
        };
    }

    if (action.type === "DONE_ADDING_NAVGUIDE_ITEM") {
        return {
            ...prevState,
            adding_navguide_item: false,
            new_navguide_item: null,
        };
    }

    if (action.type === "EDIT_NEW_NAVGUIDE_ITEM_ITEM_NAME") {
        return {
            ...prevState,
            new_navguide_item: {
                ...prevState.new_navguide_item,
                item_name: action.data.item_name,
            },
        };
    }

    if (action.type === "EDIT_NEW_NAVGUIDE_ITEM_HOURS") {
        return {
            ...prevState,
            new_navguide_item: {
                ...prevState.new_navguide_item,
                hours: action.data.hours,
            },
        };
    }

    if (action.type === "EDIT_NEW_NAVGUIDE_ITEM_EMAIL") {
        return {
            ...prevState,
            new_navguide_item: {
                ...prevState.new_navguide_item,
                email: action.data.email,
            },
        };
    }

    if (action.type === "EDIT_NEW_NAVGUIDE_ITEM_PHONE") {
        return {
            ...prevState,
            new_navguide_item: {
                ...prevState.new_navguide_item,
                phone: action.data.phone,
            },
        };
    }

    if (action.type === "EDIT_NEW_NAVGUIDE_ITEM_ADDRESS_STREET") {
        return {
            ...prevState,
            new_navguide_item: {
                ...prevState.new_navguide_item,
                address_street: action.data.address_street,
            },
        };
    }

    if (action.type === "EDIT_NEW_NAVGUIDE_ITEM_ADDRESS_CITY") {
        return {
            ...prevState,
            new_navguide_item: {
                ...prevState.new_navguide_item,
                address_city: action.data.address_city,
            },
        };
    }

    if (action.type === "EDIT_NEW_NAVGUIDE_ITEM_ADDRESS_STATE") {
        return {
            ...prevState,
            new_navguide_item: {
                ...prevState.new_navguide_item,
                address_state: action.data.address_state,
            },
        };
    }

    if (action.type === "EDIT_NEW_NAVGUIDE_ITEM_ADDRESS_ZIP") {
        return {
            ...prevState,
            new_navguide_item: {
                ...prevState.new_navguide_item,
                address_zip: action.data.address_zip,
            },
        };
    }

    if (action.type === "RECEIVE_PRDSO_COURSES") {
        let prdso_courses = {};

        for (let course of action.data.prdso_courses) {
            prdso_courses[course.prdso_course_id] = course;
        }

        return {
            ...prevState,
            prdso_courses,
        };
    }

    if (action.type === "RECEIVE_USER_PRDSO_ACTIVITY") {
        let user_prdso_activity = {};
        let prdso_course_agg_time = {};
        let prdso_deployment_agg_time_cum = 0;
        let prdso_deployment_agg_time_monthly = {};

        for (let user of action.data.user_prdso_activity) {
            let prdso_user_agg_time = 0;
            let prdso_user_agg_time_monthly = {};

            for (let enrollment of user.enrollments || []) {
                let enrollmentDedicationTime = +enrollment.dedicationtime || 0;
                prdso_user_agg_time += enrollmentDedicationTime;

                let enrollmentDate = new Date(enrollment.courseenrolmentdate);
                let enrollmentYear = enrollmentDate.getFullYear();
                let enrollmentMonth = enrollmentDate.getMonth();

                prdso_deployment_agg_time_monthly[enrollmentYear] =
                    prdso_deployment_agg_time_monthly[enrollmentYear] || [];
                prdso_deployment_agg_time_monthly[enrollmentYear][enrollmentMonth] =
                    prdso_deployment_agg_time_monthly[enrollmentYear][enrollmentMonth] || 0;
                prdso_deployment_agg_time_monthly[enrollmentYear][enrollmentMonth] += enrollmentDedicationTime;

                prdso_user_agg_time_monthly[enrollmentYear] = prdso_user_agg_time_monthly[enrollmentYear] || [];
                prdso_user_agg_time_monthly[enrollmentYear][enrollmentMonth] =
                    prdso_user_agg_time_monthly[enrollmentYear][enrollmentMonth] || 0;
                prdso_user_agg_time_monthly[enrollmentYear][enrollmentMonth] += enrollmentDedicationTime;

                prdso_course_agg_time[enrollment.prdso_course_id] =
                    prdso_course_agg_time[enrollment.prdso_course_id] || 0;
                prdso_course_agg_time[enrollment.prdso_course_id] += enrollmentDedicationTime;
            }

            prdso_deployment_agg_time_cum += prdso_user_agg_time;

            user_prdso_activity[user.vuid] = {
                ...user,
                prdso_user_agg_time,
                prdso_user_agg_time_monthly,
            };
        }
        return {
            ...prevState,
            user_prdso_activity,
            prdso_course_agg_time,
            prdso_deployment_agg_time_cum,
            prdso_deployment_agg_time_monthly,
        };
    }

    if (action.type === "START_ADDING_BULK_USERS") {
        return {
            ...prevState,
            adding_bulk_user: true,
        };
    }

    if (action.type === "DONE_SELECTING_FILE") {
        return {
            ...prevState,
            file_selected: true,
            file_data: action.data,
        };
    }

    if (action.type === "DONE_BULK_UPLOAD") {
        return {
            ...prevState,
            adding_bulk_user: false,
            file_selected: false,
            file_det: null,
        };
    }
    return prevState;
}

function triggers({ dispatch, getState }) {
    return function (next) {
        return function (action) {
            if (action.type === "SOCKET_UP") {
                const currentState = getState();

                if (currentState.pending_auth_code) {
                    socketManager.submitAuthCode({
                        auth_code: currentState.pending_auth_code,
                        location: currentState.pending_auth_code_location,
                    });
                }
            }

            if (action.type === "RECEIVE_AUTH_CODE") {
                next(action);
                const currentState = getState();

                if (currentState.socket_ready) {
                    socketManager.submitAuthCode({
                        auth_code: action.data.auth_code,
                        location: action.data.location,
                    });
                }

                return;
            }

            if (action.type === "UI_LOGIN") {
                window.location.href = buildAuthorizeUrl({
                    authorize_url: siteMeta.oauth_authorize_url,
                    client_id: siteMeta.oauth_client_id,
                    callback_url: `${window.location.protocol}//${window.location.host}/welcome`,
                });
            }

            if (action.type === "UI_LOGOUT") {
                socketManager.socket.emit("uiLogout", {}, function (res) {
                    window.location.href = buildLogoutUrl({
                        logout_url: siteMeta.oauth_logout_url,
                        client_id: siteMeta.oauth_client_id,
                        callback_url: `${window.location.protocol}//${window.location.host}/`,
                    });
                });
            }

            if (action.type === "SET_ACTIVE_DEPLOYMENT") {
                socketManager.socket.emit(
                    "setActiveDeployment",
                    {
                        deployment_id: action.data.deployment_id,
                    },
                    function (res) {
                        //
                    }
                );
            }

            if (action.type === "CLEAR_ACTIVE_DEPLOYMENT") {
                socketManager.socket.emit("clearActiveDeployment", {}, function (res) {
                    //
                });
            }

            if (action.type === "RECEIVE_DEPLOYMENT_USERS") {
                next(action);
                dispatch({
                    type: "APPLY_STAFF_USER_LIST_FILTERS",
                    data: {},
                });
                dispatch({
                    type: "APPLY_PUBLIC_USER_LIST_FILTERS",
                    data: {},
                });
                return;
            }

            if (
                [
                    "CLEAR_STAFF_USER_LIST_FILTERS_ALL",
                    "SET_STAFF_USER_LIST_FILTER_TERM",
                    "CLEAR_STAFF_USER_LIST_FILTER_TERM",
                    "TOGGLE_STAFF_USER_LIST_FILTER_DEPLOYMENT_ROLE",
                    "CLEAR_STAFF_USER_LIST_FILTER_DEPLOYMENT_ROLE",
                    "TOGGLE_STAFF_USER_LIST_FILTER_ACCOUNT_STATUS",
                    "CLEAR_STAFF_USER_LIST_FILTER_ACCOUNT_STATUS",
                    "TOGGLE_STAFF_USER_LIST_FILTER_PASSWORD_STATUS",
                    "CLEAR_STAFF_USER_LIST_FILTER_PASSWORD_STATUS",
                ].indexOf(action.type) !== -1
            ) {
                next(action);
                dispatch({
                    type: "APPLY_STAFF_USER_LIST_FILTERS",
                    data: {},
                });
                return;
            }

            if (
                [
                    "CLEAR_PUBLIC_USER_LIST_FILTERS_ALL",
                    "SET_PUBLIC_USER_LIST_FILTER_TERM",
                    "CLEAR_PUBLIC_USER_LIST_FILTER_TERM",
                    "TOGGLE_PUBLIC_USER_LIST_FILTER_DEPLOYMENT_ROLE",
                    "CLEAR_PUBLIC_USER_LIST_FILTER_DEPLOYMENT_ROLE",
                    "TOGGLE_PUBLIC_USER_LIST_FILTER_ACCOUNT_STATUS",
                    "CLEAR_PUBLIC_USER_LIST_FILTER_ACCOUNT_STATUS",
                    "TOGGLE_PUBLIC_USER_LIST_FILTER_PASSWORD_STATUS",
                    "CLEAR_PUBLIC_USER_LIST_FILTER_PASSWORD_STATUS",
                ].indexOf(action.type) !== -1
            ) {
                next(action);
                dispatch({
                    type: "APPLY_PUBLIC_USER_LIST_FILTERS",
                    data: {},
                });
                return;
            }

            next(action);
        };
    };
}
